import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useSubscription } from '@apollo/client';
import get from 'lodash/get';
import { listSubscription } from '@/graph/rooms';
import { useWebSocketReconnect } from '@/hooks/useWebSocketReconnect';
import { getGlobalAvgTimesConfiguration } from '@/graph/procedureTypes';
import { isArray, isDefinedAndNotNull } from '@/se/utilities/check';
import {
  createMapOfRoomsByType,
  mapFamilyReadyPACU,
  mapFamilyReadyPOSTOP,
  mapMonitorOperationRoom,
  mapMonitorRoom,
  mapORReady,
  mapPacuReady,
  mapPostOpReady,
  mapPrepReady,
  mapWaitingReady,
  monitorHideProcedureType,
  monitorHideProcedureTypeMessagingView,
} from '../../../entities/room/transducers';
import type {
  GraphQLQueryData,
  GraphQLSubscriptionData,
  MonitorConfiguration,
  ProcessedMonitorData,
  Room,
} from './monitor.types';

// Helper function to flatten room patients (equivalent to the legacy flattenRoomPatients)
const flattenRoomPatients = (rooms: Room[]): Room =>
  rooms
    .map(room => mapMonitorRoom(room))
    .reduce((acc, curr) => ({
      ...acc,
      patients: [...(acc.patients || []), ...(curr.patients || [])],
      capacity: (acc.capacity || 0) + (curr.capacity || 0),
    }));

// Helper function to flatten room patients as array (equivalent to flattenRoomPatients2)
const flattenRoomPatients2 = (rooms: Room[]): Room[] => rooms.map(room => mapMonitorRoom(room));

// Helper function to inject expected WR duration
const injectExpectedWRDuration = (wrRoom: Room[], expectedWRDuration: GraphQLQueryData): Room[] => [
  {
    ...wrRoom[0],
    patients: (wrRoom[0].patients || []).map(p => ({
      ...p,
      procedureType: {
        ...p.procedureType,
        expectedWRDuration: get(expectedWRDuration, 'averageTimesConfiguration.expectedWRDuration', undefined),
      },
    })),
  },
];

// Process internal monitor data (equivalent to first mapProps)
export const processInternalMonitorData = (data: GraphQLSubscriptionData | undefined) => ({
  internalMonitor: isDefinedAndNotNull(data) ? data?.internalMonitor || [] : [],
  error: (data as any)?.error,
  loading: (data as any)?.loading,
});

// Process GraphQL data with procedure type filtering (equivalent to second mapProps)
export const processGraphQLData = (
  internalMonitor: Room[],
  isLiaison: boolean,
  showProcedure: boolean
): { internalMonitor: Room[] } => ({
  internalMonitor: isLiaison
    ? monitorHideProcedureTypeMessagingView(internalMonitor, !showProcedure)
    : monitorHideProcedureType(internalMonitor, !showProcedure),
});

// Create room mappings (equivalent to third mapProps)
export const createRoomMappings = (
  internalMonitor: Room[],
  patientNameFormat: string,
  hospitalId: string,
  groupId: string
) => createMapOfRoomsByType(internalMonitor, patientNameFormat, hospitalId, groupId);

// Process room data with average times (equivalent to fourth mapProps)
export const processRoomData = (roomMappings: any, averageTimesData: GraphQLQueryData) => {
  const { operationRooms, waitingRooms, preOps, pacus, postOps } = roomMappings;

  return {
    operationRooms: isArray(operationRooms) ? operationRooms.map(mapMonitorOperationRoom) : [],
    waitingRoom: isArray(waitingRooms)
      ? flattenRoomPatients(injectExpectedWRDuration(waitingRooms, averageTimesData))
      : null,
    preOp: isArray(preOps) ? flattenRoomPatients(preOps) : null,
    pacu: isArray(pacus) ? flattenRoomPatients(pacus) : null,
    postOps: isArray(postOps) ? flattenRoomPatients2(postOps) : [],
  };
};

// Apply configuration filters (equivalent to fifth mapProps)
export const applyConfigurationFilters = (roomData: any, configuration: MonitorConfiguration) => {
  const { waitingRoom, operationRooms, preOp, pacu, postOps } = roomData;
  const config = configuration?.showOrRooms || [];
  const showPatientTypes = configuration?.showPatientTypes;

  const isConfigEmptyOrIncludes = (id: string) => !config || config.length === 0 || config.includes(id);

  const filterPatients = (room: Room | null): Room | null => {
    if (!room || !room.patients) return room;
    if (!showPatientTypes || showPatientTypes.length === 0) return room;
    return { ...room, patients: room.patients.filter(p => p.type && showPatientTypes.includes(p.type)) };
  };

  const filterOperationRoom = (room: Room): Room => {
    if (!room) return room;
    if (!showPatientTypes || showPatientTypes.length === 0) return room;
    return {
      ...room,
      patient: room.patient && room.patient.type && showPatientTypes.includes(room.patient.type) ? room.patient : null,
      patients: room.patients ? room.patients.filter(p => p.type && showPatientTypes.includes(p.type)) : [],
    };
  };

  return {
    waitingRoom: isConfigEmptyOrIncludes(waitingRoom?.id) ? mapWaitingReady(filterPatients(waitingRoom)) : null,
    operationRooms:
      config && config.length > 0
        ? mapORReady(operationRooms)
            .filter((r: Room) => config.includes(r.id))
            .map(filterOperationRoom)
        : mapORReady(operationRooms).map(filterOperationRoom),
    preOp: isConfigEmptyOrIncludes(preOp?.id) ? mapPrepReady(filterPatients(preOp)) : null,
    pacu: isConfigEmptyOrIncludes(pacu?.id) ? mapFamilyReadyPACU(mapPacuReady(filterPatients(pacu))) : null,
    postOps: (postOps ?? [])
      .filter((e: Room) => isConfigEmptyOrIncludes(e?.id))
      .map((postOp: Room) => mapFamilyReadyPOSTOP(mapPostOpReady(filterPatients(postOp)))),
  };
};

/**
 * Filter out rooms with stale timestamps.
 *
 * The Internal Monitor subscription can receive out-of-order updates because:
 * 1. Multiple events trigger parallel database fetches
 * 2. Fetches complete in unpredictable order
 * 3. An older fetch may arrive after a newer one
 *
 * This function tracks the latest _fetchedAt per room and rejects updates
 * where the incoming timestamp is older than what we've already seen.
 */
function filterStaleRooms(incomingRooms: Room[], lastSeenTimestamps: { current: Map<string, number> }): Room[] {
  const result: Room[] = [];

  for (const room of incomingRooms) {
    const roomId = room.id;
    const incomingTimestamp = room._fetchedAt;
    const lastTimestamp = lastSeenTimestamps.current.get(roomId);

    // If no timestamp, accept the room (backwards compatibility)
    if (!incomingTimestamp) {
      result.push(room);
      continue;
    }

    // If we haven't seen this room before, or the incoming data is newer, accept it
    if (!lastTimestamp || incomingTimestamp >= lastTimestamp) {
      lastSeenTimestamps.current.set(roomId, incomingTimestamp);
      result.push(room);
    } else {
      // Incoming data is older than what we have - reject it
      const staleness = lastTimestamp - incomingTimestamp;
      console.log(
        `[InternalMonitor] Rejecting stale update for room ${roomId} (${room.name}): ` +
          `incoming=${incomingTimestamp}, lastSeen=${lastTimestamp}, stale by ${staleness}ms`
      );
    }
  }

  return result;
}

// Main data processing hook that combines all transformations
export const useMonitorData = (
  patientNameFormat: string,
  hospitalId: string,
  groupId: string,
  isLiaison: boolean,
  showProcedure: boolean,
  configuration: any
): ProcessedMonitorData => {
  // Track WebSocket reconnections for logging and debugging
  const reconnectCount = useWebSocketReconnect();
  const prevReconnectCount = useRef(reconnectCount);

  // Track last seen _fetchedAt per room to detect out-of-order updates
  // This prevents "jumping" when older fetches arrive after newer ones
  const lastSeenTimestamps = useRef<Map<string, number>>(new Map());

  // Track last valid rooms to use when all incoming rooms are stale
  const lastValidRooms = useRef<Room[]>([]);

  // Log reconnection events for debugging production issues
  useEffect(() => {
    if (reconnectCount > 0 && reconnectCount !== prevReconnectCount.current) {
      console.log(
        `[InternalMonitor] WebSocket reconnected (count: ${reconnectCount}), subscription will auto-resubscribe`
      );
      prevReconnectCount.current = reconnectCount;
    }
  }, [reconnectCount]);

  // GraphQL subscriptions and queries
  // shouldResubscribe: true ensures subscription re-establishes after reconnection
  const {
    data: subscriptionData,
    loading: subscriptionLoading,
    error: subscriptionError,
  } = useSubscription(listSubscription, {
    shouldResubscribe: true,
  });
  const {
    data: averageTimesData,
    loading: averageTimesLoading,
    error: averageTimesError,
  } = useQuery(getGlobalAvgTimesConfiguration);

  // Process data through the transformation pipeline
  const processedData = useMemo(() => {
    if (subscriptionLoading || averageTimesLoading) {
      return {
        operationRooms: [],
        waitingRoom: undefined,
        preOp: undefined,
        pacu: undefined,
        postOps: [],
        loading: true,
        error: null,
      };
    }

    if (subscriptionError || averageTimesError) {
      return {
        operationRooms: [],
        waitingRoom: undefined,
        preOp: undefined,
        pacu: undefined,
        postOps: [],
        loading: false,
        error: subscriptionError || averageTimesError,
      };
    }

    // Get incoming rooms
    const incomingRooms: Room[] = subscriptionData?.internalMonitor || [];

    // Filter out stale rooms (rooms where incoming _fetchedAt < last seen _fetchedAt)
    // This prevents "jumping" caused by out-of-order database fetches
    const freshRooms = filterStaleRooms(incomingRooms, lastSeenTimestamps);

    // If we filtered out all rooms, use last valid rooms
    // This can happen if an entirely stale batch arrives
    let roomsToProcess: Room[];
    if (freshRooms.length === 0 && lastValidRooms.current.length > 0) {
      console.log('[InternalMonitor] All incoming rooms were stale, using cached rooms');
      roomsToProcess = lastValidRooms.current;
    } else if (freshRooms.length > 0) {
      // Update last valid rooms
      lastValidRooms.current = freshRooms;
      roomsToProcess = freshRooms;
    } else {
      roomsToProcess = incomingRooms;
    }

    // Step 1: Process internal monitor data (using filtered rooms)
    const internalMonitorData = {
      internalMonitor: roomsToProcess,
      error: undefined,
      loading: false,
    };

    // Step 2: Apply procedure type filtering
    const filteredData = processGraphQLData(internalMonitorData.internalMonitor, isLiaison, showProcedure);

    // Step 3: Create room mappings
    const roomMappings = createRoomMappings(filteredData.internalMonitor, patientNameFormat, hospitalId, groupId);

    // Step 4: Process room data with average times
    const processedRoomData = processRoomData(roomMappings, averageTimesData);

    // Step 5: Apply configuration filters
    const finalData = applyConfigurationFilters(processedRoomData, configuration);

    return {
      ...finalData,
      loading: false,
      error: null,
    };
  }, [
    subscriptionData,
    averageTimesData,
    subscriptionLoading,
    averageTimesLoading,
    subscriptionError,
    averageTimesError,
    patientNameFormat,
    hospitalId,
    groupId,
    isLiaison,
    showProcedure,
    configuration,
  ]);

  return processedData;
};
